import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #chatOpenObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Swak Custom - 固定カートバー内バリエーション選択機能
    this.swak_setupVariantPickerListeners();
    // /Swak Custom - 固定カートバー内バリエーション選択機能

    // Swak Custom - チャットが開いている間だけ固定カートバーを非表示
    // Horizon標準ではShopify Inboxが有効なだけで固定バーを常時非表示にするため、
    // open属性を監視して実際にチャットが開いている間だけ表示を抑制する。
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      const shopifyChat = document.querySelector('shopify-chat');
      if (!shopifyChat) return;

      const syncChatOpenState = () => {
        this.refs.stickyBar.dataset.chatOpen = shopifyChat.hasAttribute('open') ? 'true' : 'false';
      };

      syncChatOpenState();
      this.#chatOpenObserver = new MutationObserver(syncChatOpenState);
      this.#chatOpenObserver.observe(shopifyChat, {
        attributes: true,
        attributeFilter: ['open'],
      });
    });
    // /Swak Custom - チャットが開いている間だけ固定カートバーを非表示
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#chatOpenObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Swak Custom - 追従カート非表示トリガーをfooter-utilitiesに変更
    // const footer = document.querySelector('footer-group');
    const footer = document.querySelector('.footer-utilities');
    // /Swak Custom - 追従カート非表示トリガーをfooter-utilitiesに変更
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Swak Custom - チャットの開閉はdata-chat-open属性とCSSで制御する
          // if (this.#isChatActive()) return;
          // /Swak Custom - チャットの開閉はdata-chat-open属性とCSSで制御する
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            // Swak Custom - チャットの開閉はdata-chat-open属性とCSSで制御する
            // if (!this.#isChatActive()) {
            this.#showStickyBar();
            // }
            // /Swak Custom - チャットの開閉はdata-chat-open属性とCSSで制御する
          }
        }
      },
      {
        /* rootMargin: '200px', Swak Custom - 追従カート非表示トリガーをfooter-utilitiesに変更 */
        rootMargin: '0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // Swak Custom - ボタンリセット処理の改善
    // 問題: onAnimationEndが完了しない場合、ボタンが永遠にチェックマークのままになる
    // 解決策: 
    // 1. 最初に2秒のフォールバックタイムアウトを設定（必ず戻る保証）
    // 2. アニメーション完了後はフォールバックをキャンセルし、800msの適切なタイムアウトに切り替え
    // 3. エラー時はフォールバックがリセットを処理
    const fallbackTimeoutId = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 2000); // 2秒後に強制リセット（フォールバック）
    // /Swak Custom - ボタンリセット処理の改善

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await yieldToMainThread();

    try {
      // アニメーション完了を待つ
      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

      // アニメーションが正常に完了したのでフォールバックタイムアウトをキャンセル
      clearTimeout(fallbackTimeoutId);

      // 適切なタイミング（800ms）でチェックマークからテキストに戻す
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    } catch (error) {
      console.error('Animation end error:', error);
      // エラー時はフォールバックタイムアウト（2秒）がリセットを処理
    }
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Swak Custom - バリエーション選択中は固定バーのボタンを無効化
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
    }
    // /Swak Custom - バリエーション選択中は固定バーのボタンを無効化

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        // Swak Custom - バリエーション更新完了後、固定バーのボタンを有効化
        if (this.refs.addToCartButton) {
          if (variant && variant.available) {
            this.refs.addToCartButton.disabled = false;
          } else {
            this.refs.addToCartButton.disabled = true;
          }
        }
        // /Swak Custom - バリエーション更新完了後、固定バーのボタンを有効化

        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */(option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  // Swak Custom - 固定カートバー内バリエーション選択機能メソッド
  /**
   * 固定カートバー用バリエーションピッカーのイベントリスナーを設定
   */
  swak_setupVariantPickerListeners() {
    const { signal } = this.#abortController;
    const stickyVariantPicker = this.querySelector('.swak-stickyVariantPicker');
    if (!stickyVariantPicker) return;

    // ドロップダウン形式のselect要素の変更イベントをリスン
    const select = stickyVariantPicker.querySelector('.swak-stickyVariantSelect');
    if (select) {
      select.addEventListener('change', this.swak_handleStickyVariantChange, { signal });
    }
  }

  /**
   * 固定カートバー内のバリエーションselect変更を処理
   * @param {Event} event - 変更イベント
   */
  swak_handleStickyVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const selectedVariantId = select.value;

    // 固定バーのボタンを即座に無効化
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
    }

    // メインのvariant-pickerを更新
    this.swak_updateMainVariantPicker(selectedVariantId);
  }

  /**
   * Swak Custom - メインのvariant-pickerを更新
   * @param {string} variantId - 選択されたバリエーションID
   */
  swak_updateMainVariantPicker(variantId) {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');

    if (!sectionElement) return;

    // メインのvariant-pickerを検索
    const mainVariantPicker = sectionElement.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!mainVariantPicker) return;

    // 商品URLを取得
    const productUrl = mainVariantPicker.dataset.productUrl;
    if (!productUrl) return;

    // バリアントIDでURLを構築
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    // メインのvariant-pickerのfetchUpdatedSectionメソッドを呼び出す
    if (typeof mainVariantPicker.fetchUpdatedSection === 'function') {
      mainVariantPicker.fetchUpdatedSection(url.href);
    }
  }
  // /Swak Custom - 固定カートバー内バリエーション選択機能メソッド
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
